"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { AlertTriangle, X } from "lucide-react";
import { TriangleAlert } from "lucide-react";

/**
 * Props for the ConfirmationDialog component
 */
interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Dialog title text */
  title: string;
  /** Optional description text */
  description?: string;
  /** Confirm button text. Defaults to "Confirm". */
  confirmText?: string;
  /** Cancel button text. Defaults to "Cancel". */
  cancelText?: string;
  /** Callback fired when confirm is clicked */
  onConfirm: () => void;
  /** Callback fired when cancel is clicked or dialog is closed */
  onCancel: () => void;
  /** Visual variant of the dialog. Defaults to "default". */
  variant?: "default" | "warning";
}

/**
 * ConfirmationDialog component displays a confirmation dialog with title, description,
 * and confirm/cancel buttons. Supports default and warning variants. Closes on cancel
 * or when clicking outside.
 * 
 * @component
 * @param {ConfirmationDialogProps} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {string} props.title - Dialog title
 * @param {string} [props.description] - Optional description
 * @param {string} [props.confirmText="Confirm"] - Confirm button text
 * @param {string} [props.cancelText="Cancel"] - Cancel button text
 * @param {() => void} props.onConfirm - Confirm callback
 * @param {() => void} props.onCancel - Cancel callback
 * @param {"default" | "warning"} [props.variant="default"] - Visual variant
 * @returns {JSX.Element} Confirmation dialog
 * 
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   open={isOpen}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item?"
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 *   variant="warning"
 * />
 * ```
 */
export default function ConfirmationDialog({
  open,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "default",
}: ConfirmationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="w-[45rem]">
        <DialogHeader>
          <DialogTitle className="m-0 flex gap-3 items-center text-[red]"><TriangleAlert color="red"/>{title}</DialogTitle>
        </DialogHeader>
        {description && <div>{description}</div>}
        <DialogFooter className="sm:justify-between">
          <DialogClose
            asChild
            onClick={onCancel}
          >
            <button className="px-4 py-2 rounded-md border text-sm hover:bg-gray-100">
              {cancelText}
            </button>
          </DialogClose>

          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md text-sm ${
              variant === "warning"
                ? "bg-primary-500 text-white hover:bg-primary-600"
                : "bg-primary-600 text-white hover:bg-primary-700"
            }`}
          >
            {confirmText}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
